import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-white/80 py-6 px-4 text-center text-xs" data-id="element-39">
        <div className="max-w-2xl mx-auto space-y-3" data-id="element-40">
          <p className="leading-relaxed" data-id="element-41">
            Disclaimer: The information provided in this guide is for
            educational purposes only and is not intended as a substitute for
            advice from your physician or other health care professional. You
            should not use the information in this guide for diagnosis or
            treatment of any health problem or for prescription of any
            medication or other treatment.
          </p>
          <div className="flex items-center justify-center gap-3" data-id="element-42">
            <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-43">
              Privacy Policy
            </button>
            <span className="text-white/40" data-id="element-44">|</span>
            <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-45">
              Terms of Service
            </button>
          </div>
          <p data-id="element-46">
            &copy; {new Date().getFullYear()} The 10-Minute Anti-Inflammatory
            Ritual. All rights reserved.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-47">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} data-id="element-48">
            <div className="absolute inset-0 bg-black/60" onClick={() => setActiveModal(null)} data-id="element-49" />

            <motion.div className="relative bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[80vh] overflow-y-auto z-10" initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 15,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} data-id="element-50">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between rounded-t-xl" data-id="element-51">
                <h2 className="text-lg font-bold text-forest" data-id="element-52">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-53">
                  <XIcon className="w-4 h-4 text-charcoal" data-id="element-54" />
                </button>
              </div>

              <div className="px-5 py-4 text-charcoal text-sm leading-relaxed space-y-3" data-id="element-55">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-56" /> : <TermsContent data-id="element-57" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-gray-500" data-id="element-58">Last updated: April 2026</p>
      <h3 className="text-base font-bold text-forest" data-id="element-59">
        1. Information We Collect
      </h3>
      <p data-id="element-60">
        When you purchase our digital guide, we collect only the information
        necessary to process your order, such as your email address and payment
        details. Payment processing is handled by secure third-party providers —
        we do not store your credit card information.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-61">
        2. How We Use Your Information
      </h3>
      <p data-id="element-62">
        We use your email address solely to deliver your purchased digital
        product and to send order confirmations. We will not send unsolicited
        marketing emails unless you explicitly opt in.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-63">
        3. Information Sharing
      </h3>
      <p data-id="element-64">
        We do not sell, trade, or rent your personal information to third
        parties. We may share information only with trusted service providers
        who assist us in delivering your purchase.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-65">4. Cookies</h3>
      <p data-id="element-66">
        Our website may use cookies to improve your browsing experience and
        analyze site traffic. You can control cookie preferences through your
        browser settings.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-67">5. Data Security</h3>
      <p data-id="element-68">
        We implement reasonable security measures to protect your personal
        information. However, no method of electronic transmission or storage is
        100% secure.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-69">6. Your Rights</h3>
      <p data-id="element-70">
        You have the right to request access to, correction of, or deletion of
        your personal information at any time.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-71">
        7. Changes to This Policy
      </h3>
      <p data-id="element-72">
        We reserve the right to update this Privacy Policy at any time. Changes
        will be posted on this page with an updated revision date.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-gray-500" data-id="element-73">Last updated: April 2026</p>
      <h3 className="text-base font-bold text-forest" data-id="element-74">1. Digital Product</h3>
      <p data-id="element-75">
        This product is a digital PDF eBook delivered electronically. Upon
        purchase, you will receive immediate access to download the guide. No
        physical product will be shipped.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-76">2. Permitted Use</h3>
      <p data-id="element-77">
        Your purchase grants you a personal, non-transferable license to use the
        guide for your own personal, non-commercial purposes. You may not
        reproduce, distribute, resell, or share the content.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-78">3. Health Disclaimer</h3>
      <p data-id="element-79">
        The information in this guide is for educational and informational
        purposes only. It is not intended as medical advice and should not
        replace consultation with a qualified healthcare professional.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-80">4. No Guarantees</h3>
      <p data-id="element-81">
        Results may vary from person to person. We make no guarantees regarding
        specific health outcomes.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-82">5. Refund Policy</h3>
      <p data-id="element-83">
        Due to the digital nature of this product, all sales are final. If you
        experience technical issues accessing your guide, please reach out and
        we will ensure you receive your download.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-84">
        6. Intellectual Property
      </h3>
      <p data-id="element-85">
        All content is the intellectual property of The 10-Minute
        Anti-Inflammatory Ritual and is protected by copyright law.
      </p>
      <h3 className="text-base font-bold text-forest" data-id="element-86">
        7. Changes to These Terms
      </h3>
      <p data-id="element-87">
        We reserve the right to modify these Terms of Service at any time.
        Continued use of the product after changes constitutes acceptance of the
        updated terms.
      </p>
    </>;
}